var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Color, Mesh, MeshStandardMaterial, Object3D, PMREMGenerator, Scene, Texture, RepeatWrapping, MeshPhysicalMaterial, TorusKnotGeometry, } from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { MaterialMaps } from '@canvas-logic/clp-domain';
export class MaterialManagerView3D {
    constructor(canvas, cameraManager, renderer, material, hdr) {
        this.canvas = canvas;
        this.cameraManager = cameraManager;
        this.renderer = renderer;
        this.material = material;
        this.hdr = hdr;
        this.resizeHandler = () => {
            this.updateSizes();
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        this.hdrPath = hdr;
        const { color, roughness, metalness, opacity, transparent } = material;
        this.mat = new MeshStandardMaterial({ color, roughness, metalness, opacity, transparent });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraManager.init({
                camera: {
                    minDistance: 10,
                    maxDistance: 60,
                    maxPolarAngle: 180,
                    enablePan: false,
                    position: { x: 0, y: 0, z: 40 },
                },
            }, this.canvas);
            this.initScene();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.initHandlers();
            this.startRendering();
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.background = new Color(0xffffff);
        const pMREMGenerator = new PMREMGenerator(this.renderer);
        this.scene.environment = pMREMGenerator.fromScene(new RoomEnvironment()).texture;
        this.scene.add(this.sceneNode);
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        this.loadView();
    }
    loadView() {
        const geometry = new TorusKnotGeometry(5, 1.5, 200, 32);
        this.mesh = new Mesh(geometry, this.mat);
        this.rootNode.add(this.mesh);
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    startRendering() {
        requestAnimationFrame(() => {
            this.cameraManager.update();
            if (this.renderer) {
                this.renderer.render(this.scene, this.cameraManager.camera);
                this.startRendering();
            }
        });
    }
    setMaterial(material) {
        var _a, _b;
        let newMaterial;
        if (material.materialType === 'MeshPhysicalMaterial') {
            newMaterial = new MeshPhysicalMaterial({
                color: material.color,
                roughness: material.roughness,
                metalness: material.metalness,
                opacity: material.opacity,
                transparent: material.transparent,
                emissive: material.emissive,
                emissiveIntensity: material.emissiveIntensity,
                clearcoat: (_a = material.clearcoat) !== null && _a !== void 0 ? _a : 0,
                clearcoatRoughness: (_b = material.clearcoatRoughness) !== null && _b !== void 0 ? _b : 0,
            });
        }
        else {
            newMaterial = new MeshStandardMaterial({
                color: material.color,
                roughness: material.roughness,
                metalness: material.metalness,
                opacity: material.opacity,
                transparent: material.transparent,
                emissive: material.emissive,
                emissiveIntensity: material.emissiveIntensity,
            });
        }
        const updateTexture = (prop, texture) => {
            var _a, _b;
            if (newMaterial[prop]) {
                newMaterial[prop].dispose();
            }
            newMaterial[prop] = texture || null;
            if (texture) {
                texture.needsUpdate = true;
                texture.wrapS = texture.wrapT = RepeatWrapping;
                texture.repeat.set((_a = material.repeatX) !== null && _a !== void 0 ? _a : 1, (_b = material.repeatY) !== null && _b !== void 0 ? _b : 1);
            }
        };
        for (const map of Object.values(MaterialMaps)) {
            const materialMap = material[map];
            if (materialMap) {
                updateTexture(map, new Texture(materialMap));
            }
            else {
                updateTexture(map, undefined);
            }
        }
        newMaterial.needsUpdate = true;
        this.mesh.material = newMaterial;
    }
}
//# sourceMappingURL=MaterialManagerView3D.js.map